/**
 * ProfileRequiredRoute Component
 *
 * Wrapper component that requires both authentication AND a completed profile.
 * If onboarding is NOT complete and profile is incomplete, redirects to /onboarding.
 * If onboarding IS complete and profile is incomplete, redirects to /account.
 *
 * Required profile fields:
 * - first_name, pronouns
 * - At least one conversation partner (primary partner)
 *
 * These fields are needed for proper speaker token replacement in LLM-generated content.
 */

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { usePrimaryPartner } from "../hooks/useConversationPartners";
import { useOnboarding } from "../hooks/useOnboarding";
import { LoadingSpinner } from "./LoadingSpinner";
import { checkProfileCompletion } from "../utils/profileCompletion";

interface ProfileRequiredRouteProps {
  children: React.ReactNode;
}

export function ProfileRequiredRoute({ children }: ProfileRequiredRouteProps) {
  const {
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
  } = useAuth();
  const {
    data: user,
    isLoading: userLoading,
    isFetching: userFetching,
  } = useCurrentUser();
  const {
    data: primaryPartner,
    isLoading: partnerLoading,
    isFetching: partnerFetching,
  } = usePrimaryPartner();
  const { isOnboardingComplete, isLoading: onboardingLoading } =
    useOnboarding();
  const location = useLocation();

  // Check auth first
  if (authLoading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    loginWithRedirect();
    return <LoadingSpinner message="Redirecting to login..." />;
  }

  // Wait for user data, partners, and onboarding state to fully load
  if (
    userLoading ||
    partnerLoading ||
    userFetching ||
    partnerFetching ||
    onboardingLoading
  ) {
    return <LoadingSpinner message="Loading your profile..." />;
  }

  // If onboarding is not complete, redirect to onboarding wizard
  if (!isOnboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check profile completion (first_name, pronouns)
  const { isComplete: userFieldsComplete } = checkProfileCompletion(user);

  // Also require at least one conversation partner
  const hasRequiredPartner = !!primaryPartner;

  if (!userFieldsComplete || !hasRequiredPartner) {
    // Redirect to account page with a state indicating they need to complete profile
    // and where to return after completion
    return (
      <Navigate
        to="/account"
        state={{
          profileIncomplete: true,
          returnTo: location.pathname,
        }}
        replace
      />
    );
  }

  return <>{children}</>;
}
